import type { Item, ItemId, Project, Scenario, ScenarioId } from '@finsim/model';
import { useUnit } from 'effector-react';
import {
  ChevronRight,
  Copy,
  Eye,
  EyeOff,
  Focus,
  Layers,
  Link2,
  MoreHorizontal,
  Pencil,
  Plus,
  SlidersHorizontal,
  Star,
  Trash2,
} from 'lucide-react';
import { useState, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  $project,
  groupedItems,
  ItemIcon,
  itemSummary,
  usageCount,
  type ItemSummary,
  $readOnly,
} from '../../entities/project';
import { $visibility, visibilityKey } from '../../entities/item-visibility';
import {
  $inspector,
  assumptionsOpened,
  sharedItemsOpened,
  itemSelected,
  scenarioFocused,
  scenarioInspected,
} from '../../entities/selection';
import { $result, type Diagnostic } from '../../entities/simulation';
import { $hiddenScenarios, scenarioVisibilityToggled } from '../../entities/view-settings';
import { AddItemMenu } from '../../features/add-item';
import { copyToRequested, ItemContextMenuItems, shareRequested } from '../../features/item-actions';
import {
  addVariantRequested,
  deleteVariantRequested,
  duplicateVariantRequested,
  setBaselineRequested,
} from '../../features/manage-variants';
import { cn } from '../../shared/lib/cn';
import { formatMoney, formatPercent } from '../../shared/lib/format';
import { useLocale } from '../../shared/lib/i18n/use-locale';
import {
  Button,
  ContextMenu,
  ContextMenuContent,
  ContextMenuTrigger,
  EmptyState,
  IconButton,
  Menu,
  MenuContent,
  MenuItem,
  MenuSeparator,
  MenuTrigger,
  Tooltip,
} from '../../shared/ui';
import { $addMenuFor, $collapsed, addMenuClosed, addMenuOpened, variantToggled } from './model';

type Severity = 'error' | 'warning' | 'info';

/** Drag payload type for moving items between variants. */
const DRAG_TYPE = 'application/x-finsim-item';

function readDrag(raw: string): { itemId: ItemId; scenarioId: ScenarioId } | null {
  try {
    const v = JSON.parse(raw) as { itemId?: unknown; scenarioId?: unknown };
    return typeof v.itemId === 'string' && typeof v.scenarioId === 'string'
      ? { itemId: v.itemId as ItemId, scenarioId: v.scenarioId as ScenarioId }
      : null;
  } catch {
    return null;
  }
}

function useSummaryText(): (summary: ItemSummary, currency: string) => string {
  const { t } = useTranslation();
  const locale = useLocale();
  return (summary, currency) => {
    if (summary.kind === 'percent') return formatPercent(summary.percent, locale);
    if (summary.kind === 'none') return '';
    const money = formatMoney(summary.sign * summary.amount, locale, {
      currency,
      compact: summary.amount >= 1_000_000,
    }).replace('-', '−');
    return summary.cadence && summary.cadence !== 'once'
      ? `${money}${t(`cadence.${summary.cadence}`)}`
      : money;
  };
}

export function VariantsPanel() {
  const { t } = useTranslation();
  const [project, readOnly, inspector, addVariant, openAssumptions, openShared] = useUnit([
    $project,
    $readOnly,
    $inspector,
    addVariantRequested,
    assumptionsOpened,
    sharedItemsOpened,
  ]);
  const newVariant = (): void => {
    addVariant({ name: t('variants.defaultName', { n: (project?.scenarios.length ?? 0) + 1 }) });
  };

  return (
    <nav aria-label={t('variants.title')} data-tour="tree" className="flex h-full flex-col bg-bg-1">
      <div className="flex h-9 shrink-0 items-center justify-between pr-1.5 pl-3">
        <h2 className="text-xs font-medium text-text-3">{t('variants.title')}</h2>
        {!readOnly && (
          <Tooltip content={t('variants.newVariant')} shortcut={['⇧', 'C']}>
            <IconButton
              label={t('variants.newVariant')}
              size="sm"
              disabled={!project}
              onClick={newVariant}
            >
              <Plus />
            </IconButton>
          </Tooltip>
        )}
      </div>

      <div className="min-h-0 flex-1 overflow-y-auto px-1.5 pb-2">
        {project && project.scenarios.length > 0 ? (
          <ul className="space-y-0.5">
            {project.scenarios.map((scenario) => (
              <VariantNode key={scenario.id} project={project} scenario={scenario} />
            ))}
          </ul>
        ) : (
          <EmptyState
            icon={<Layers />}
            title={t('variants.emptyTitle')}
            body={t('variants.emptyBody')}
            className="h-full py-8"
            {...(project && !readOnly
              ? {
                  action: (
                    <Button size="sm" leadingIcon={<Plus />} onClick={newVariant}>
                      {t('variants.createFirst')}
                    </Button>
                  ),
                }
              : {})}
          />
        )}
      </div>

      <div className="shrink-0 space-y-0.5 border-t border-border-subtle p-1.5">
        <FooterRow
          icon={<Link2 />}
          label={t('variants.sharedItems')}
          active={inspector?.kind === 'shared'}
          disabled={!project}
          onClick={openShared}
        />
        <FooterRow
          icon={<SlidersHorizontal />}
          label={t('variants.assumptions')}
          active={inspector?.kind === 'assumptions'}
          disabled={!project}
          onClick={openAssumptions}
        />
      </div>
    </nav>
  );
}

function FooterRow({
  icon,
  label,
  onClick,
  active = false,
  disabled = false,
  hint,
}: {
  icon: ReactNode;
  label: string;
  onClick?: () => void;
  active?: boolean;
  disabled?: boolean;
  hint?: string;
}) {
  const button = (
    <button
      type="button"
      disabled={disabled}
      aria-current={active || undefined}
      onClick={onClick}
      className={cn(
        'flex h-7 w-full items-center gap-2 rounded-md px-2 text-left text-sm text-text-2 outline-none',
        'hover:bg-bg-3 focus-visible:ring-2 focus-visible:ring-focus disabled:opacity-50 [&_svg]:size-4 [&_svg]:text-text-3',
        active && 'bg-bg-4 text-text-1',
      )}
    >
      {icon}
      <span className="truncate">{label}</span>
    </button>
  );
  return hint ? (
    <Tooltip content={hint} side="right">
      <span className="block">{button}</span>
    </Tooltip>
  ) : (
    button
  );
}

function VariantNode({ project, scenario }: { project: Project; scenario: Scenario }) {
  const { t } = useTranslation();
  const [
    collapsed,
    inspector,
    addMenuFor,
    toggle,
    inspect,
    focus,
    openAdd,
    closeAdd,
    duplicate,
    remove,
    setBaseline,
  ] = useUnit([
    $collapsed,
    $inspector,
    $addMenuFor,
    variantToggled,
    scenarioInspected,
    scenarioFocused,
    addMenuOpened,
    addMenuClosed,
    duplicateVariantRequested,
    deleteVariantRequested,
    setBaselineRequested,
  ]);
  const [share, copyTo, readOnly] = useUnit([shareRequested, copyToRequested, $readOnly]);
  const [hiddenScenarios, toggleVisible] = useUnit([$hiddenScenarios, scenarioVisibilityToggled]);
  const hidden = hiddenScenarios.has(scenario.id);
  const [dropHint, setDropHint] = useState<'share' | 'copy' | null>(null);
  const isCollapsed = collapsed.has(scenario.id);
  const isBaseline = project.baselineScenarioId === scenario.id;

  // Drop an item from another variant here: share it (⌥/Alt: copy it).
  const dragProps = {
    onDragOver: (e: React.DragEvent) => {
      if (!e.dataTransfer.types.includes(DRAG_TYPE)) return;
      e.preventDefault();
      e.dataTransfer.dropEffect = e.altKey ? 'copy' : 'link';
      setDropHint(e.altKey ? 'copy' : 'share');
    },
    onDragLeave: () => {
      setDropHint(null);
    },
    onDrop: (e: React.DragEvent) => {
      setDropHint(null);
      const payload = readDrag(e.dataTransfer.getData(DRAG_TYPE));
      if (!payload || payload.scenarioId === scenario.id) return;
      e.preventDefault();
      if (e.altKey) copyTo({ itemId: payload.itemId, scenarioId: scenario.id });
      else if (!scenario.itemIds.includes(payload.itemId))
        share({ itemId: payload.itemId, scenarioId: scenario.id });
    },
  };
  const inspected = inspector?.kind === 'scenario' && inspector.scenarioId === scenario.id;
  const groups = groupedItems(project, scenario.id);
  const color = `var(--chart-series-${String(scenario.color + 1)})`;

  return (
    <li>
      <div
        {...(readOnly ? {} : dragProps)}
        className={cn(
          'group flex h-7 items-center gap-1 rounded-md pr-1 hover:bg-bg-3',
          inspected && 'bg-bg-4 hover:bg-bg-4',
          dropHint && 'bg-accent-subtle ring-1 ring-accent',
        )}
        title={
          dropHint
            ? t(dropHint === 'copy' ? 'variants.dropCopy' : 'variants.dropShare', {
                name: scenario.name,
              })
            : undefined
        }
      >
        <button
          type="button"
          aria-expanded={!isCollapsed}
          aria-label={
            isCollapsed
              ? t('variants.expand', { name: scenario.name })
              : t('variants.collapse', { name: scenario.name })
          }
          onClick={() => {
            toggle(scenario.id);
          }}
          className="flex size-6 shrink-0 items-center justify-center rounded-sm text-text-3 outline-none hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus"
        >
          <ChevronRight
            className={cn(
              'size-3.5 transition-transform duration-150',
              !isCollapsed && 'rotate-90',
            )}
          />
        </button>
        <button
          type="button"
          onClick={() => {
            inspect(scenario.id);
          }}
          className="flex min-w-0 flex-1 items-center gap-2 text-left text-sm font-medium outline-none focus-visible:underline"
        >
          <span
            aria-hidden
            className="size-2.5 shrink-0 rounded-full"
            style={{ background: color }}
          />
          <span className={cn('truncate', hidden && 'text-text-3')}>{scenario.name}</span>
          {isBaseline && (
            <Star
              aria-label={t('variants.baseline')}
              className="size-3 shrink-0 fill-current text-text-4"
            />
          )}
        </button>
        <Tooltip content={hidden ? t('variants.showInCharts') : t('variants.hideFromCharts')}>
          <IconButton
            label={t('variants.inCharts', { name: scenario.name })}
            size="sm"
            aria-pressed={!hidden}
            onClick={() => {
              toggleVisible(scenario.id);
            }}
            className={cn(
              !hidden && 'opacity-0 group-focus-within:opacity-100 group-hover:opacity-100',
            )}
          >
            {hidden ? <EyeOff /> : <Eye />}
          </IconButton>
        </Tooltip>
        {!readOnly && (
          <div className="flex items-center opacity-0 group-focus-within:opacity-100 group-hover:opacity-100 has-[[aria-expanded=true]]:opacity-100">
            <AddItemMenu
              scenarioId={scenario.id}
              open={addMenuFor === scenario.id}
              onOpenChange={(open) => {
                if (open) {
                  openAdd(scenario.id);
                  focus(scenario.id);
                } else closeAdd();
              }}
            >
              <IconButton label={t('variants.addItem')} size="sm">
                <Plus />
              </IconButton>
            </AddItemMenu>
            <Menu>
              <MenuTrigger asChild>
                <IconButton label={t('variants.actions')} size="sm">
                  <MoreHorizontal />
                </IconButton>
              </MenuTrigger>
              <MenuContent align="start">
                <MenuItem
                  icon={<Pencil />}
                  onSelect={() => {
                    inspect(scenario.id);
                  }}
                >
                  {t('variants.edit')}
                </MenuItem>
                <MenuItem
                  icon={<Copy />}
                  onSelect={() => {
                    duplicate({
                      scenarioId: scenario.id,
                      mode: 'share',
                      name: t('variants.copyName', { name: scenario.name }),
                    });
                  }}
                >
                  {t('variants.duplicateShared')}
                </MenuItem>
                <MenuItem
                  icon={<Copy />}
                  onSelect={() => {
                    duplicate({
                      scenarioId: scenario.id,
                      mode: 'copy',
                      name: t('variants.copyName', { name: scenario.name }),
                    });
                  }}
                >
                  {t('variants.duplicateCopy')}
                </MenuItem>
                {!isBaseline && (
                  <MenuItem
                    icon={<Star />}
                    onSelect={() => {
                      setBaseline({ scenarioId: scenario.id });
                    }}
                  >
                    {t('variants.setBaseline')}
                  </MenuItem>
                )}
                <MenuSeparator />
                <MenuItem
                  icon={<Trash2 />}
                  onSelect={() => {
                    remove({ scenarioId: scenario.id });
                  }}
                >
                  {t('variants.delete')}
                </MenuItem>
              </MenuContent>
            </Menu>
          </div>
        )}
      </div>

      {!isCollapsed && (
        <div className="pb-1">
          {groups.length === 0 ? (
            <p className="py-1 pl-9 text-xs text-text-3">{t('variants.noItems')}</p>
          ) : (
            groups.map(({ group, items }) => (
              <div key={group} className="mt-1">
                <p className="h-6 pl-9 text-2xs leading-6 font-medium text-text-3">
                  {t(`groups.${group}`)}
                </p>
                <ul>
                  {items.map((item) => (
                    <ItemRow key={item.id} project={project} item={item} scenarioId={scenario.id} />
                  ))}
                </ul>
              </div>
            ))
          )}
        </div>
      )}
    </li>
  );
}

function ItemRow({
  project,
  item,
  scenarioId,
}: {
  project: Project;
  item: Item;
  scenarioId: ScenarioId;
}) {
  const { t } = useTranslation();
  const summaryText = useSummaryText();
  const [inspector, result, select, visibility, readOnly] = useUnit([
    $inspector,
    $result,
    itemSelected,
    $visibility,
    $readOnly,
  ]);
  const muted = visibility.muted.has(visibilityKey(scenarioId, item.id));
  const solo = visibility.soloed.get(scenarioId);
  const soloed = solo === item.id;
  const dimmed = muted || (solo !== undefined && !soloed);
  const selected =
    inspector?.kind === 'item' &&
    inspector.itemId === item.id &&
    inspector.scenarioId === scenarioId;
  const shared = (usageCount(project).get(item.id) ?? 0) > 1;
  const diagnostics = result?.scenarios[scenarioId]?.diagnostics ?? [];
  const severity = worstSeverity(diagnostics, item.id);
  const problem = firstProblem(diagnostics, item.id);

  return (
    <li>
      <ContextMenu>
        <ContextMenuTrigger asChild>
          <button
            type="button"
            draggable={!readOnly}
            onDragStart={(e) => {
              e.dataTransfer.setData(DRAG_TYPE, JSON.stringify({ itemId: item.id, scenarioId }));
              e.dataTransfer.effectAllowed = 'copyLink';
            }}
            data-item-row={`${scenarioId}:${item.id}`}
            aria-current={selected || undefined}
            onClick={() => {
              select({ itemId: item.id, scenarioId });
            }}
            className={cn(
              'flex h-7 w-full items-center gap-2 rounded-md pr-2 pl-9 text-left text-sm text-text-2 outline-none',
              'hover:bg-bg-3 hover:text-text-1 focus-visible:ring-2 focus-visible:ring-focus',
              selected && 'bg-bg-4 text-text-1 hover:bg-bg-4',
              dimmed && 'opacity-50',
            )}
          >
            <ItemIcon item={item} className="size-3.5 shrink-0 text-text-3" />
            <span className={cn('min-w-0 flex-1 truncate', muted && 'line-through')}>
              {item.name}
            </span>
            {muted && (
              <EyeOff aria-label={t('visibility.muted')} className="size-3 shrink-0 text-text-3" />
            )}
            {soloed && (
              <Focus aria-label={t('visibility.soloed')} className="size-3 shrink-0 text-accent" />
            )}
            {shared && (
              <Link2 aria-label={t('itemActions.shared')} className="size-3 shrink-0 text-text-4" />
            )}
            {severity && severity !== 'info' && problem && (
              <span
                role="img"
                aria-label={t(`diagnostics.${problem.code}`)}
                className={cn(
                  'size-1.5 shrink-0 rounded-full',
                  severity === 'error' ? 'bg-status-critical' : 'bg-status-warning',
                )}
              />
            )}
            <span className="tabular shrink-0 text-xs text-text-3">
              {summaryText(itemSummary(item), project.assumptions.currency)}
            </span>
          </button>
        </ContextMenuTrigger>
        <ContextMenuContent>
          <ItemContextMenuItems itemId={item.id} scenarioId={scenarioId} />
        </ContextMenuContent>
      </ContextMenu>
    </li>
  );
}

function worstSeverity(diagnostics: readonly Diagnostic[], itemId: ItemId): Severity | null {
  let worst: Severity | null = null;
  for (const d of diagnostics) {
    if (d.itemId !== itemId) continue;
    if (d.severity === 'error') return 'error';
    if (d.severity === 'warning') worst = 'warning';
    else worst ??= 'info';
  }
  return worst;
}

function firstProblem(diagnostics: readonly Diagnostic[], itemId: ItemId): Diagnostic | undefined {
  return diagnostics.find((x) => x.itemId === itemId && x.severity !== 'info');
}
